import React, {useEffect, useReducer} from 'react';
import {Button, Col, Form, InputGroup, Modal, Row} from '@themesberg/react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCar, faPen, faPowerOff, faTrashCan} from '@fortawesome/free-solid-svg-icons';
import {FormattedMessage} from 'react-intl';
import Utils from '../services/Utils';
import {useHistory} from 'react-router-dom';
import API from '../services/API';
import Error from '../services/Error';
import PageHeader from '../components/PageHeader';
import CrudTable from '../components/CrudTable';
import TranslationsField, {LanguageChip, NameWithTranslations, countTranslations} from '../components/TranslationsField';
import {usePlan} from '../services/PlanContext';


export default (props) => {

  const formReducer = (state, event) => {
    if (event.value !== undefined) {
      return {
        ...state,
        [event.name]: event.value
      }
    } else {
      return event;
    }
  }

  const {intl} = props;
  const {plan, resourceLimit, openSubscription} = usePlan();
  const history = useHistory();
  const [showModal, setShowModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [editModalMode, setEditModalMode] = React.useState(false);
  const [currentVehicleId, setCurrentVehicleId] = React.useState(false);
  const [currentVehicleName, setCurrentVehicleName] = React.useState('');
  const [vehicles, setVehicles] = React.useState([]);
  // Limite formule gratuite : calculée sur la liste locale (à jour après création/suppression), et non
  // sur le compteur du contexte plan (rafraîchi seulement au montage → sinon on reste bloqué à tort).
  const locked = plan === 'FREE' && resourceLimit != null && vehicles.length >= resourceLimit;
  const [formData, setFormData] = useReducer(formReducer, {});
  const [currency, setCurrency] = React.useState({code: 'EUR', symbol: '€'});
  const [formatter, setFormatter] = React.useState(new Intl.NumberFormat(navigator.language, {style: 'currency', currency: 'EUR'}));
  const [lengthUnit, setLengthUnit] = React.useState('KM');
  // Langue du nom principal (langue par défaut du module) : les autres langues sont saisies dans le bloc Traductions.
  const [defaultLanguage, setDefaultLanguage] = React.useState('fr');
  // Bloc Traductions déplié à l'ouverture seulement si le véhicule en a déjà ; modalOpenCount sert de key
  // au bloc pour repartir de cet état à chaque ouverture (la modale n'est jamais démontée).
  const [translationsOpen, setTranslationsOpen] = React.useState(false);
  const [modalOpenCount, setModalOpenCount] = React.useState(0);

  const refreshVehicles = () => {
    API.getVehicles(history).then(response => {
      if (response && response.data.success === true) {
        const vehicles = response.data.data.vehicles.sort(function (a, b) {
          return a.position - b.position
        });
        setVehicles(vehicles);
        setLengthUnit(response.data.data.lengthUnit === 'MILE' ? 'MI' : 'KM');
        if (response.data.data.defaultLanguage) {
          setDefaultLanguage(response.data.data.defaultLanguage);
        }
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  useEffect(() => {
    if (!Utils.isAccessToken(history)) {
      return;
    }
    refreshVehicles();
    API.getCurrency(history).then(response => {
      if (response && response.data.success === true) {
        setFormatter(new Intl.NumberFormat(navigator.language, {
          style: 'currency',
          currency: response.data.data.code,
        }));
        setCurrency(response.data.data);
      }
    });
  }, []);

  const handleAddOrEdit = (e) => {
    e.preventDefault();
    if (editModalMode) {
      API.putVehicles(history, formData, currentVehicleId).then(response => {
        if (response && response.data.success === true) {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'SAVED_CHANGES'}));
          setShowModal(false);
          refreshVehicles();
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    } else {
      API.postVehicles(history, formData).then(response => {
        if (response && response.data.success === true) {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'VEHICLE_ADDED'}));
          setShowModal(false);
          refreshVehicles();
        } else if (response && response.data && response.data.data === Error.planLimitReached()) {
          // Filet : si le plafond gratuit est atteint (course avec le chargement du plan), on ferme le
          // formulaire et on propose de passer au payant au lieu d'un toast d'erreur générique.
          setShowModal(false);
          openSubscription('RESOURCE_LIMIT');
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    }
  }

  const handleChange = event => {
    setFormData({
      name: event.target.id,
      value: event.target.value
    });
  }

  const handleChangeTranslations = (translations) => {
    setFormData({
      name: 'translations',
      value: translations
    });
  }

  const handleNewVehicleButton = () => {
    setFormData({});
    setTranslationsOpen(false);
    setModalOpenCount(c => c + 1);
    setEditModalMode(false);
    setShowModal(true);
  }

  const handleEditVehicleButton = (props) => {
    setFormData(props);
    setTranslationsOpen(countTranslations(props.translations) > 0);
    setModalOpenCount(c => c + 1);
    setCurrentVehicleId(props.id);
    setEditModalMode(true);
    setShowModal(true);
  }

  const handleEnableOrDisableButton = (props) => {
    if (props.active) {
      API.getDisableVehicle(history, props.id).then(response => {
        if (response && response.data.success === true) {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'VEHICLE_DISABLED'}));
          refreshVehicles();
        } else if (response && response.data.success === false && response.data.data === Error.lastVehicleMustNotBeDisabled()) {
          Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'YOU_CANT_DISABLE_LAST_VEHICLE'}));
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    } else {
      API.getEnableVehicle(history, props.id).then(response => {
        if (response && response.data.success === true) {
          // Gratuit : activer un 2ᵉ véhicule bascule l'actif au lieu de bloquer -> toast dédié.
          const switched = response.data.data === Error.resourceSwitched();
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: switched ? 'VEHICLE_SWITCHED' : 'VEHICLE_ENABLED'}));
          setShowDeleteModal(false);
          refreshVehicles();
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    }
  }

  const handleDeleteVehicleButton = (props) => {
    setCurrentVehicleId(props.id);
    setCurrentVehicleName(props.name);
    setShowDeleteModal(true);
  }

  // a little function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    // update position variable
    let index = 0;
    for (const [key, value] of Object.entries(result)) {
      value.position = index++;
    }

    return result;
  };

  const handleDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(
      vehicles,
      result.source.index,
      result.destination.index
    );

    setVehicles(items);

    // Prepare data for backend
    let data = {
      vehicles: []
    };

    for (const [key, value] of Object.entries(items)) {
      data.vehicles.push({
        id: value.id,
        position: value.position
      });
    }

    API.postReorderVehicles(history, data).then(response => {
      if (response && response.data.success === true) {
        // Everything is OK
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  const deleteVehicle = () => {
    API.deleteVehicles(history, currentVehicleId).then(response => {
      if (response && response.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'VEHICLE_DELETED'}));
        setShowDeleteModal(false);
        refreshVehicles();
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  return (
    <div className="ccv2-crud">
      <PageHeader title="VEHICLES" description="VEHICLES_DESC"/>

      <CrudTable
        draggable
        getRowId={(v) => String(v.id)}
        onDragEnd={handleDragEnd}
        items={vehicles}
        intl={intl}
        columns={[
          {key: 'name', header: 'NAME', priority: 1, render: (v) => <NameWithTranslations name={v.name} translations={v.translations}/>},
          {key: 'priceFrom', header: 'PRICE_FROM', headerMultiline: true, numeric: true, priority: 2, render: (v) => formatter.format(v.priceFrom)},
          {key: 'euroPerKm', header: `${currency.symbol} / ${lengthUnit}`, numeric: true, priority: 2, render: (v) => formatter.format(v.euroPerKm)},
          {key: 'euroPerMinute', header: `${currency.symbol} / ${intl.formatMessage({id: 'MINUTE'})}`, numeric: true, priority: 3, hideBelow: 'xl', render: (v) => formatter.format(v.euroPerMinute)},
          {key: 'euroPerPassenger', header: `${currency.symbol} / ${intl.formatMessage({id: 'PASSENGER'})}`, numeric: true, priority: 3, hideBelow: 'xl', render: (v) => formatter.format(v.euroPerPassenger)},
          {key: 'minFare', header: 'MIN_COST', numeric: true, priority: 3, hideBelow: 'xl', render: (v) => formatter.format(v.minFare)},
          {key: 'passengersSpace', header: 'PASSENGERS_SPACE', headerMultiline: true, numeric: true, priority: 3, hideBelow: 'xl'},
          {key: 'baggageSpace', header: 'BAGGAGE_SPACE', headerMultiline: true, numeric: true, priority: 3, hideBelow: 'xl'},
          {key: 'numberOwned', header: 'NUMBER_OWNED', headerMultiline: true, numeric: true, priority: 3, hideBelow: 'xl'},
          {key: 'active', header: 'ACTIVE_TAB', type: 'status', priority: 2},
        ]}
        actions={[
          {key: 'edit', icon: faPen, label: 'EDIT', onClick: handleEditVehicleButton},
          {key: 'toggle', icon: faPowerOff, label: (v) => (v.active ? 'DISABLE' : 'ENABLE'), intent: 'warn', onClick: handleEnableOrDisableButton},
          {key: 'delete', icon: faTrashCan, label: 'DELETE', intent: 'danger', onClick: handleDeleteVehicleButton},
        ]}
        toolbar={{onNew: handleNewVehicleButton, newLabel: 'NEW_VEHICLE', locked}}
        empty={{icon: faCar, title: 'EMPTY_VEHICLES_TITLE', description: 'EMPTY_VEHICLES_DESC'}}
      />

      <Modal as={Modal.Dialog} centered show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6">{editModalMode ? <FormattedMessage id="EDIT_VEHICLE"/> : <FormattedMessage id="NEW_VEHICLE"/>}</Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowModal(false)}/>
        </Modal.Header>
        <Modal.Body>
          <Form id="add-vehicle-form" onSubmit={handleAddOrEdit}>
            <Row>
              <Form.Group>
                <Form.Label className="cc-label-with-lang">
                  <FormattedMessage id="NAME"/>
                  <LanguageChip code={defaultLanguage}/>
                </Form.Label>
                <Form.Control required type="text" id="name" maxLength={255}
                              onChange={handleChange} value={formData.name || ''}/>
              </Form.Group>
              <TranslationsField key={modalOpenCount} idPrefix="vehicle-translation"
                                 value={formData.translations || {}}
                                 onChange={handleChangeTranslations}
                                 defaultLanguage={defaultLanguage}
                                 baseName={formData.name || ''}
                                 defaultOpen={translationsOpen}/>
            </Row>
            <br/>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label><FormattedMessage id="PASSENGERS_SPACE" values={{newLine: ' '}}/></Form.Label>
                  <Form.Control required type="number" id="passengersSpace" min="0"
                                onChange={handleChange} value={formData.passengersSpace}/>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label><FormattedMessage id="BAGGAGE_SPACE" values={{newLine: ' '}}/></Form.Label>
                  <Form.Control required type="number" id="baggageSpace" min="0"
                                onChange={handleChange} value={formData.baggageSpace}/>
                </Form.Group>
              </Col>
            </Row>
            <br/>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label><FormattedMessage id="NUMBER_OWNED" values={{newLine: ' '}}/></Form.Label>
                  <Form.Control required type="number" id="numberOwned" min="0"
                                onChange={handleChange} value={formData.numberOwned}/>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label><FormattedMessage id="PRICE_FROM" values={{newLine: ' '}}/></Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={Utils.getCurrencyIcon(currency.code)}/>
                    </InputGroup.Text>
                    <Form.Control type="number" id="priceFrom" step="any" min="0" max="99999999.99"
                                  onChange={handleChange} value={formData.priceFrom}/>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <br/>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>{currency.symbol} / {lengthUnit}</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={Utils.getCurrencyIcon(currency.code)}/>
                    </InputGroup.Text>
                    <Form.Control type="number" id="euroPerKm" step="any" min="0" max="99999999.99"
                                  onChange={handleChange} value={formData.euroPerKm}/>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>{currency.symbol} / <FormattedMessage id="MINUTE"/></Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={Utils.getCurrencyIcon(currency.code)}/>
                    </InputGroup.Text>
                    <Form.Control type="number" id="euroPerMinute" step="any" min="0" max="99999999.99"
                                  onChange={handleChange} value={formData.euroPerMinute}/>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <br/>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>{currency.symbol} / <FormattedMessage id="PASSENGER"/></Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={Utils.getCurrencyIcon(currency.code)}/>
                    </InputGroup.Text>
                    <Form.Control type="number" id="euroPerPassenger" step="any" min="0" max="99999999.99"
                                  onChange={handleChange} value={formData.euroPerPassenger}/>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label><FormattedMessage id="MIN_COST"/></Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={Utils.getCurrencyIcon(currency.code)}/>
                    </InputGroup.Text>
                    <Form.Control type="number" id="minFare" step="any" min="0" max="99999999.99"
                                  onChange={handleChange} value={formData.minFare}/>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" type="submit" form="add-vehicle-form">
            {editModalMode ? <FormattedMessage id="RECORD"/> : <FormattedMessage id="ADD"/>}
          </Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowModal(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal as={Modal.Dialog} centered show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6"><FormattedMessage id="DELETE_VEHICLE" values={{name: currentVehicleName}}/></Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowDeleteModal(false)}/>
        </Modal.Header>
        <Modal.Footer>
          <Button variant="danger" onClick={deleteVehicle}>
            <FormattedMessage id="DELETE"/>
          </Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowDeleteModal(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
